import { A } from '@ember/array';
import Controller from '@ember/controller';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import type Store from '@ember-data/store';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency';
import type { PaginatedRecordArray } from 'garaje/infinity-models/v3-offset';
import type EmployeeModel from 'garaje/models/employee';
import type FloorModel from 'garaje/models/floor';
import type StateService from 'garaje/services/state';
import employeesSearcherTask, { type EmployeeSearcherTask } from 'garaje/utils/employees-searcher';
interface EmployeeQuery {
  page: { limit: number; offset: number };
  filter: { locations: string; deleted: boolean };
  include: string;
  sort: string;
}

type EmployeeItem = EmployeeModel | 'loadMore';
export default class ResourceTypeIndexController extends Controller {
  @tracked showCreateResourceModal = false;

  @tracked employees: EmployeeItem[] = [];
  @tracked searchedEmployees: EmployeeModel[] = [];
  @tracked employeesPage = 0;
  @tracked employeesCount = 0;
  @tracked limit = 25;

  @tracked floors: FloorModel[] = [];
  @tracked reloadTableCallback: (() => Promise<void>) | null = null;

  @service declare store: Store;
  @service declare state: StateService;

  get employeeQuery(): EmployeeQuery {
    const { limit, employeesPage } = this;
    const offset = employeesPage * limit;
    return {
      page: { limit, offset },
      filter: {
        locations: this.state.currentLocation.id,
        deleted: false,
      },
      include: 'assistants',
      sort: 'name',
    };
  }

  get hasMoreEmployeePages(): boolean {
    return this.employeesPage * this.limit < this.employeesCount;
  }

  loadEmployeesTask = task({ enqueue: true }, async () => {
    const employees = <PaginatedRecordArray<EmployeeModel>>await this.store.query('employee', this.employeeQuery);
    this.employeesCount = employees.meta.total;
    const employeeArray = employees.slice();

    // Filter out 'loadMore' and existing employees, then add new ones
    const existingEmployees = this.employees.filter((el): el is EmployeeModel => el !== 'loadMore');
    const uniqueEmployees = [...existingEmployees, ...employeeArray].reduce(
      (acc: EmployeeModel[], curr: EmployeeModel) => {
        if (!acc.find((e) => e.email === curr.email)) {
          acc.push(curr);
        }
        return acc;
      },
      [],
    );

    this.employees = uniqueEmployees;
    this.employeesPage++;
    if (this.hasMoreEmployeePages) {
      A(this.employees).pushObject('loadMore');
    }
  });

  loadMoreEmployeesTask = task({ drop: true }, async () => {
    if (!this.hasMoreEmployeePages) return;
    try {
      await this.loadEmployeesTask.perform();
    } catch (_) {
      this.employeesPage--;
    }
  });

  @(employeesSearcherTask({
    filter: {
      deleted: false,
    },
    prefix: true,
  }).restartable())
  _searchEmployeesTask!: EmployeeSearcherTask;
  employeesSearcherTask = task({ restartable: true }, async (term: string) => {
    const extraFilters = {
      locations: this.state.currentLocation.id,
      deleted: false,
    };
    this.searchedEmployees = await this._searchEmployeesTask.perform(term, extraFilters);
  });

  onEmployeeSearchTask = task({ restartable: true }, async (searchTerm: string) => {
    if (searchTerm) {
      await this.employeesSearcherTask.perform(searchTerm);
    }
    const validEmployee = this.searchedEmployees.find(
      (employee) => searchTerm.toLowerCase().trim() === employee.name?.toLowerCase(),
    );
    if (validEmployee) {
      return;
    }
    if (searchTerm === '') {
      this.searchedEmployees = [];
      return;
    }
  });

  get employeesToShow(): EmployeeItem[] {
    if (this.searchedEmployees.length) {
      return this.searchedEmployees;
    }
    return this.employees;
  }

  loadFloorsTask = task({ drop: true }, async (): Promise<void> => {
    const floors = await this.store.query('floor', {
      filter: { 'location-id': this.state.currentLocation.id },
    });
    this.floors = floors.slice();
  });

  @action
  handleSetReloadTableCallback(callback: () => Promise<void>): void {
    this.reloadTableCallback = callback;
  }

  @action
  openCreateResourceModal(): void {
    this.showCreateResourceModal = true;
  }

  @action
  closeCreateResourceModal(): void {
    this.showCreateResourceModal = false;
    if (this.reloadTableCallback) {
      void this.reloadTableCallback();
    }
  }
}
